/**
 * Phase 7B — Prescriptions Mini Panel
 *
 * Quick-action overlay for medications. Shows med list with status,
 * allows quick send/queue/dismiss. No deep configuration.
 * Full editing lives on the Finalization Page.
 */
import React from 'react';
import { api } from '../../services/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  X, SendHorizonal, Clock, Check, Pill, ArrowRight, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

export default function MiniPanelPrescriptions({
  enc, outputs, encounterId, fetchEncounter, setEncounter,
  onClose, onOpenFull,
}) {
  const meds = (outputs?.chart_updates || []).filter(cu => cu.update_type?.startsWith('medication'));

  if (meds.length === 0) {
    return (
      <MiniPanelShell onClose={onClose} onOpenFull={onOpenFull} title="Prescriptions">
        <div className="text-center py-4">
          <Pill className="w-5 h-5 mx-auto mb-1.5 text-muted-foreground/30" />
          <p className="text-[10px] text-muted-foreground">No medications detected</p>
        </div>
      </MiniPanelShell>
    );
  }

  return (
    <MiniPanelShell onClose={onClose} onOpenFull={onOpenFull} title="Prescriptions" count={meds.length}>
      <div className="space-y-1.5" data-testid="mini-panel-prescriptions-list">
        {meds.map((cu, i) => (
          <MedQuickRow
            key={cu.id || i}
            cu={cu}
            index={i}
            encounterId={encounterId}
            setEncounter={setEncounter}
            fetchEncounter={fetchEncounter}
          />
        ))}
      </div>
    </MiniPanelShell>
  );
}


function MedQuickRow({ cu, index, encounterId, setEncounter, fetchEncounter }) {
  const [loading, setLoading] = React.useState(false);
  const actionLabel = (cu.medication_action || 'new').replace(/_/g, ' ');
  const isHandled = cu.disposition && cu.disposition !== 'unreviewed';

  const actionColor = actionLabel === 'new' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : actionLabel === 'continued' ? 'bg-sky-50 text-sky-700 border-sky-200'
    : actionLabel === 'dose change' ? 'bg-amber-50 text-amber-700 border-amber-200'
    : actionLabel === 'discontinued' ? 'bg-red-50 text-red-700 border-red-200'
    : 'bg-muted text-muted-foreground';

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const res = await api.post(`/encounters/${encounterId}/medications/${cu.id}/execute`, {
        medication_id: cu.id,
        action,
        edits: {},
      });
      setEncounter(res.data.encounter);
      const labels = { send: 'Sent', queue: 'Queued', dismiss: 'Dismissed' };
      toast.success(`${cu.medication_name} — ${labels[action] || action}`);
    } catch {
      toast.error('Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`rounded-md border px-2.5 py-2 ${isHandled ? 'opacity-60' : ''}`}
      data-testid={`mini-med-${index}`}>
      <div className="flex items-center gap-2">
        <Badge variant="outline" className={`text-[8px] h-4 px-1 shrink-0 ${actionColor}`}>
          {actionLabel === 'new' ? 'Start' : actionLabel === 'continued' ? 'Refill' : actionLabel === 'discontinued' ? 'Stop' : actionLabel}
        </Badge>
        <span className="text-[11px] font-medium flex-1 min-w-0 truncate">
          {cu.medication_name || cu.description}
        </span>
        {cu.medication_details && (
          <span className="text-[9px] text-muted-foreground shrink-0">{cu.medication_details}</span>
        )}
      </div>

      {!isHandled ? (
        <div className="flex gap-1 mt-1.5">
          <Button size="sm" className="h-5 text-[9px] px-2 gap-0.5 flex-1"
            disabled={loading}
            onClick={() => handleAction('send')}
            data-testid={`mini-med-send-${index}`}>
            {loading ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <SendHorizonal className="w-2.5 h-2.5" />}
            Send
          </Button>
          <Button size="sm" variant="outline" className="h-5 text-[9px] px-2 gap-0.5"
            disabled={loading}
            onClick={() => handleAction('queue')}
            data-testid={`mini-med-queue-${index}`}>
            <Clock className="w-2.5 h-2.5" /> Queue
          </Button>
          <Button size="sm" variant="ghost" className="h-5 text-[9px] px-1 text-muted-foreground"
            disabled={loading}
            onClick={() => handleAction('dismiss')}
            data-testid={`mini-med-dismiss-${index}`}>
            <X className="w-2.5 h-2.5" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-1 mt-1 text-[9px]">
          {cu.disposition === 'queued' ? (
            <><Clock className="w-2.5 h-2.5 text-amber-500" /><span className="text-amber-600">Queued</span></>
          ) : cu.disposition === 'approve' || cu.disposition === 'sent' ? (
            <><Check className="w-2.5 h-2.5 text-emerald-500" /><span className="text-emerald-600">Sent</span></>
          ) : (
            <><X className="w-2.5 h-2.5 text-slate-400" /><span className="text-slate-500">Dismissed</span></>
          )}
        </div>
      )}
    </div>
  );
}


function MiniPanelShell({ children, onClose, onOpenFull, title, count }) {
  return (
    <div className="mt-1.5 mb-1 rounded-lg border border-primary/20 bg-card shadow-sm animate-in fade-in-0 slide-in-from-top-1 duration-150"
      data-testid={`mini-panel-${title?.toLowerCase()}`}>
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/50">
        <span className="text-[10px] font-semibold text-foreground flex items-center gap-1.5">
          {title}
          {count > 0 && <Badge variant="outline" className="text-[8px] h-3.5 px-1">{count}</Badge>}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={onOpenFull} className="text-[9px] text-primary hover:underline flex items-center gap-0.5"
            data-testid="mini-panel-open-full">
            Full view <ArrowRight className="w-2.5 h-2.5" />
          </button>
          <button onClick={onClose} className="p-0.5 rounded hover:bg-accent" data-testid="mini-panel-close">
            <X className="w-3 h-3 text-muted-foreground" />
          </button>
        </div>
      </div>
      <div className="px-3 py-2 max-h-64 overflow-y-auto">
        {children}
      </div>
    </div>
  );
}
